/* eslint-disable */
import type React from 'react';
import { useCallback } from 'react';
import { useEffect } from 'react';
import { useState } from 'react';

import InputBase, { type InputBaseProps } from '@material-ui/core/InputBase';
import { withStyles } from '@material-ui/core/styles';

import { colors, fonts } from '../../styles';

export interface InputProps extends InputBaseProps {
  debounceInMillis?: number;
}

const shadow = (color: string) =>
  `0 0 10px 0 ${colors.shadows.getShadow(color)}`;

const StyledInput = withStyles({
  root: {
    height: 40,
    padding: '9px',
    boxSizing: 'border-box',
    borderWidth: 1,
    borderStyle: 'solid',
    borderRadius: 4,
    borderColor: colors.gray.light,
    fontSize: fonts.size.small,
    fontWeight: fonts.weight.regular,
    backgroundColor: 'white',
  },
  multiline: {
    height: 'unset',
    minHeight: 40,
  },
  adornedStart: {
    '& > svg': {
      fontSize: fonts.size.medium,
      color: colors.gray.default,
    },
  },
  adornedEnd: {
    '& > svg': {
      fontSize: fonts.size.medium,
      color: colors.gray.default,
    },
  },
  inputAdornedStart: {
    marginLeft: 10,
  },
  input: {
    fontSize: fonts.size.small,
    fontWeight: fonts.weight.regular,
    color: colors.black,
    padding: 'unset',
    textOverflow: 'ellipsis',
    '&::placeholder': {
      opacity: 1,
      color: colors.gray.dark,
      fontWeight: fonts.weight.light,
    },
    '&[type=number]': {
      '-moz-appearance': 'textfield',
    },
    '&::-webkit-outer-spin-button': {
      '-webkit-appearance': 'none',
      margin: 0,
    },
    '&::-webkit-inner-spin-button': {
      '-webkit-appearance': 'none',
      margin: 0,
    },
  },
  focused: {
    borderColor: colors.black,
    boxShadow: shadow(colors.gray.default),
  },
  error: {
    '&&': {
      borderColor: colors.error.default,
      '&$focused': {
        boxShadow: shadow(colors.error.default),
      },
    },
  },
  disabled: {
    '&&': {
      background: colors.gray.lightest,
      cursor: 'not-allowed',
      color: colors.gray.dark,
      fontWeight: fonts.weight.light,
    },
  },
})(InputBase);

export const DefaultInput: React.FC<InputProps> = ({
  debounceInMillis,
  value,
  onChange,
  ...otherProps
}) => {
  const [debouncedValue, setDebouncedValue] = useState<string>(
    (value as string) || ''
  );
  const [timer, setTimer] = useState<number | null>(null);

  const onNewChangeEvent = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      event.persist();
      setDebouncedValue(event.target.value);

      if (timer) {
        clearTimeout(timer);
      }

      setTimer(
        window.setTimeout(() => {
          if (onChange) {
            onChange(event);
          }
        }, debounceInMillis)
      );
    },
    [timer, debounceInMillis, onChange]
  );

  useEffect(() => {
    if (debounceInMillis !== undefined && debouncedValue !== value) {
      setDebouncedValue((value as string) || '');
    }
  }, [value]);

  return (
    <StyledInput
      {...otherProps}
      value={debounceInMillis ? debouncedValue : value}
      onChange={debounceInMillis ? onNewChangeEvent : onChange}
    />
  );
};
